import { Event, EventResponseList } from "@/app/types/event";
import { apiSlice } from "@/features/api/ApiSlice";

export const EventApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getEvents: builder.query<
      EventResponseList,
      { org_id: number; status?: string; query?: string }
    >({
      query: ({ org_id, status, query }) => {
        let queryString = `org_id=${org_id}`;
        if (status) queryString += `&status=${status}`;
        if (query) queryString += `&${query}`; // Keeping existing query logic if needed, but primary filters are org_id and status
        return {
          url: `/events/?${queryString}`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        };
      },
      providesTags: ["Event"],
    }),
    getEventById: builder.query<Event, number>({
      query: (event_id) => ({
        url: `/events/${event_id}`,
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      }),
      providesTags: (result, error, event_id) => [
        { type: "Event", id: event_id },
      ],
    }),
    createEvent: builder.mutation<Event, Partial<Event>>({
      query: (body) => ({
        url: "/events/create",
        method: "POST",
        body,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: ["Event"],
    }),
    updateEvent: builder.mutation<
      Event,
      { event_id: number; body: Partial<Event> }
    >({
      query: ({ event_id, body }) => ({
        url: `/events/${event_id}`,
        method: "PUT",
        body,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: (result, error, { event_id }) => [
        "Event",
        { type: "Event", id: event_id },
      ],
    }),
    updateEventStatus: builder.mutation<
      Event,
      { event_id: number; status: string }
    >({
      query: ({ event_id, status }) => ({
        url: `/events/${event_id}/status`,
        method: "PATCH",
        body: { status },
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: (result, error, { event_id }) => [
        "Event",
        { type: "Event", id: event_id },
      ],
    }),
    deleteEvent: builder.mutation<Event, number>({
      query: (event_id) => ({
        url: `/events/${event_id}`,
        method: "DELETE",
        headers: {
          Accept: "application/json",
        },
      }),
      invalidatesTags: ["Event"],
    }),
    getAllEvent: builder.query<EventResponseList, { org_id: number; query?: string }>({
      query: ({ org_id, query }) => ({
        url: `/events/?org_id=${org_id}&status=active&is_live=true${query ? "&"+query : ""}`,
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      }),
      providesTags: ["Event"],
    }),
  }),
});

export const {
  useGetEventsQuery,
  useGetEventByIdQuery,
  useCreateEventMutation,
  useUpdateEventMutation,
  useUpdateEventStatusMutation,
  useDeleteEventMutation,
  useGetAllEventQuery,
} = EventApi;
